import { FC } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { GetProgramFeeResponse } from "../../../../api/services/common/models";
import { MuiBox, MuiDoubleLabel, MuiGrid } from "../../../../components";
import { BillingTypeConfigureTable } from "../../../../components/billingTypeConfigureTable/BillingTypeConfigureTable";
import {
  CURRENCY_FORMAT_MAP,
  CurrencyFormat,
} from "../../../../components/textFieldCurrency/CurrencyUtils";
import { gridSize } from "../../../../helpers/constants";
import {
  getCurrencyNameAndLocaleById,
  getFormattedAmount,
  isActivityBasedBilling,
  isEmpty,
  isMilestoneBilling,
  isScheduledBasedBilling,
} from "../../../../helpers/utils";
import { useForceUpdate } from "../../../../hooks/useForceUpdate";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { currencyListSelector } from "../../../../redux/slices/appState/appStateSelector";
import { billingConfigurationDetailsFormKeys } from "../../../createProgram/modelsAndSchemas/Models";
import { projectFeeDetailsCoreKeys } from "../../../createProject/siteCore/SiteCoreKeys";
import { ViewProjectFeeWrapper } from "./ViewProjectFee.style";

interface ViewProjectFeeProps {
  projectFeeDetailsData?: GetProgramFeeResponse;
  programTemplateId: string;
  clientId: string;
  pageId: string;
}

export const ViewProjectFee: FC<ViewProjectFeeProps> = ({
  projectFeeDetailsData,
  programTemplateId,
  clientId,
  pageId,
}) => {
  const { commonLabels, fetchLabel, fetchValueByKeyOfDropdown } = useSiteCore();

  const currencyList = useSelector(currencyListSelector);

  const forceUpdate = useForceUpdate();

  const {
    projectFeeDetails,
    earlyPayDays,
    earlyPayPercentage,
    minimumTransferFee,
    billingTriggerDropDown,
    currencyDropDown,
    projectFee,
    billingTriggerList,
    billingTypeList,
    billingTypeDropDown,
    proBonoCategoryDropDown,
    proBonoCategoryList,
  } = projectFeeDetailsCoreKeys;

  const getProjectFeeLabel = (key: string) => {
    return fetchLabel(projectFeeDetails, key);
  };

  const shouldShowTableConfiguration = () => {
    return (
      projectFeeDetailsData?.billingTypeId &&
      (isScheduledBasedBilling(projectFeeDetailsData?.billingTypeId) ||
        isMilestoneBilling(projectFeeDetailsData?.billingTypeId))
    );
  };

  const getDropdownValues = (key: string, value?: string) => {
    if (!value || isEmpty(value)) return "";
    const data = fetchValueByKeyOfDropdown(projectFeeDetails, key, value);

    return data;
  };

  const getEarlyPayPercentageValue = () => {
    if (
      !projectFeeDetailsData?.earlyPayPercentage ||
      isEmpty(projectFeeDetailsData?.earlyPayPercentage)
    ) {
      return "";
    }
    return `${projectFeeDetailsData?.earlyPayPercentage}%`;
  };

  const getEarlyPayDaysValue = () => {
    if (
      !projectFeeDetailsData?.earlyPayDays ||
      isEmpty(projectFeeDetailsData?.earlyPayDays)
    ) {
      return "";
    }
    return `${projectFeeDetailsData?.earlyPayDays} ${projectFeeDetailsData?.earlyPayDays > 1 ? commonLabels.days : commonLabels.day}`;
  };

  const getCurrencyNameValue = () => {
    return projectFeeDetailsData?.currency?.name ?? "";
  };

  const getCurrencyFormat = () => {
    const currencyFormat: CurrencyFormat =
      CURRENCY_FORMAT_MAP[getCurrencyNameValue()];
    return currencyFormat ?? "";
  };

  const getFormattedValue = (value: number): string => {
    const currencyData = getCurrencyNameAndLocaleById(
      currencyList,
      projectFeeDetailsData?.currency?.id,
    );

    const formattedAmount = `${getCurrencyNameValue()} ${getFormattedAmount(value, currencyData?.currencyLocale)}`;
    return formattedAmount;
  };

  const getProjectFee = () => {
    if (!projectFeeDetailsData?.fee || isEmpty(projectFeeDetailsData?.fee)) {
      return "";
    }
    return getFormattedValue(projectFeeDetailsData?.fee);
  };

  const getMinimumTransferFeeValue = () => {
    if (
      !projectFeeDetailsData?.minimumTransferFee ||
      isEmpty(projectFeeDetailsData?.minimumTransferFee)
    ) {
      return "";
    }
    return getFormattedValue(projectFeeDetailsData?.minimumTransferFee);
  };

  return (
    <ViewProjectFeeWrapper>
      <MuiBox className="content">
        <MuiGrid container spacing={2}>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getProjectFeeLabel(billingTypeDropDown)}
              data-testid="billing-type-label"
              replaceEmptyWithDash
              value={getDropdownValues(
                billingTypeList,
                projectFeeDetailsData?.billingTypeId,
              )}
            />
          </MuiGrid>

          {isActivityBasedBilling(
            projectFeeDetailsData?.billingTypeId ?? "",
          ) && (
            <>
              <MuiGrid size={gridSize}>
                <MuiDoubleLabel
                  data-testid="billing-trigger-label"
                  label={getProjectFeeLabel(billingTriggerDropDown)}
                  replaceEmptyWithDash
                  value={getDropdownValues(
                    billingTriggerList,
                    projectFeeDetailsData?.billingTriggerId,
                  )}
                />
              </MuiGrid>

              <MuiGrid size={gridSize} className="form-field-normal" />
            </>
          )}

          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              data-testid="currency-label"
              label={getProjectFeeLabel(currencyDropDown)}
              replaceEmptyWithDash
              value={getCurrencyNameValue()}
            />
          </MuiGrid>

          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              data-testid="program-fee-label"
              label={getProjectFeeLabel(projectFee)}
              replaceEmptyWithDash
              value={getProjectFee()}
            />
          </MuiGrid>

          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              data-testid="minimum-transfer-fee-label"
              label={getProjectFeeLabel(minimumTransferFee)}
              replaceEmptyWithDash
              value={getMinimumTransferFeeValue()}
            />
          </MuiGrid>

          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              data-testid="early-pay-days-label"
              label={getProjectFeeLabel(earlyPayDays)}
              replaceEmptyWithDash
              value={getEarlyPayDaysValue()}
            />
          </MuiGrid>

          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              data-testid="early-pay-percentage-label"
              label={getProjectFeeLabel(earlyPayPercentage)}
              replaceEmptyWithDash
              value={getEarlyPayPercentageValue()}
            />
          </MuiGrid>

          {isActivityBasedBilling(
            projectFeeDetailsData?.billingTypeId ?? "",
          ) && <MuiGrid size={gridSize} className="form-field-normal" />}

          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              // data-testid="pro-bono-category-label"
              label={getProjectFeeLabel(proBonoCategoryDropDown)}
              replaceEmptyWithDash
              value={getDropdownValues(
                proBonoCategoryList,
                projectFeeDetailsData?.proBonoCategoryId,
              )}
            />
          </MuiGrid>
        </MuiGrid>
        {shouldShowTableConfiguration() && (
          <BillingTypeConfigureTable
            pageId={pageId}
            data-testid="billing-config-table"
            currencyName={getCurrencyNameValue()}
            currencyFormat={getCurrencyFormat()}
            currencyId={projectFeeDetailsData?.currency?.id ?? ""}
            forceUpdate={forceUpdate}
            programTemplateId={programTemplateId}
            isFromBillingInvoice={true}
            billingConfigurationDetailsFormKeys={
              billingConfigurationDetailsFormKeys
            }
            clientId={clientId}
            isViewOnly={true}
            isFromProject
          />
        )}
      </MuiBox>
    </ViewProjectFeeWrapper>
  );
};
